import { useLocation, Link, useRoute } from "wouter";
import { useEffect, useState } from "react";
import { Users, ShieldCheck, ArrowRight, Sparkles, Lock } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { useBranding } from "@/hooks/use-branding";
import { Card, CardContent } from "@/components/ui/card";
import {
  getStaffPortalBlockedMessage,
  getStaffPortalReadiness,
  type StaffPortalReadiness,
} from "@/lib/staff-portal-readiness";

// Standalone entry point for a chatbot's test portal. It is intentionally
// auth-independent: it is launched from a chatbot's detail page so the person
// can preview how that bot behaves for the public vs. for staff.
//   - Guest  → public chat window (pre-chat form, no sign-in). Enabled only
//              when the bot allows public access.
//   - Staff  → full-screen chat as the signed-in user. No role/area is picked;
//              the server resolves the user's role from their login session and
//              tailors answers to it. Enabled only when the bot has a staff side.
// Legacy: when reached at "/portal" (no bot), it falls back to the platform
// public landing / sign-in choice.
const PUBLIC_LANDING = "/org/platform";

export default function PortalGateway() {
  const [, setLocation] = useLocation();
  const [, botParams] = useRoute("/portal/:apiKey");
  const apiKey = botParams?.apiKey;
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const branding = useBranding();

  const [accessMode, setAccessMode] = useState<string | null>(null);
  const [hasIntegrations, setHasIntegrations] = useState(false);
  const [staffReadiness, setStaffReadiness] = useState<StaffPortalReadiness | null>(null);
  const [readinessMessage, setReadinessMessage] = useState<string | null>(null);
  const [botName, setBotName] = useState<string | null>(null);
  const [loading, setLoading] = useState(!!apiKey);

  useEffect(() => {
    if (!apiKey) return;
    let cancelled = false;
    fetch(`/api/v1/chatbots/${apiKey}/config`)
      .then((r) => (r.ok ? r.json() : null))
      .then((cfg) => {
        if (cancelled || !cfg) return;
        setAccessMode(cfg.accessMode || "authenticated");
        setHasIntegrations(!!cfg.hasIntegrations);
        setStaffReadiness(getStaffPortalReadiness(cfg));
        setReadinessMessage(getStaffPortalBlockedMessage(cfg));
        setBotName(cfg.name || null);
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [apiKey]);

  // A public bot remains reachable as a guest. Staff readiness is authoritative
  // from the server and is intentionally independent of integration presence:
  // AI-only and KB-only bots are supported; tool-only bots are not.
  const guestEnabled = apiKey ? accessMode === "public" || !hasIntegrations : true;
  const staffEnabled = apiKey ? staffReadiness?.isReady === true : true;

  const onGuest = () => {
    if (!guestEnabled) return;
    // ?guest=1 forces the widget to open as a true anonymous visitor (pre-chat
    // contact form first), even though a signed-in admin launched the portal —
    // otherwise the platform session would be treated as staff and skip the form.
    setLocation(apiKey ? `/widget/${apiKey}?guest=1` : PUBLIC_LANDING);
  };
  const onStaff = () => {
    if (!staffEnabled) return;
    if (apiKey) {
      // Staff don't pick a role or area by hand — the assistant runs as the
      // signed-in user and tailors its answers to that user's own role, which
      // the server resolves from the login session. Go straight to the
      // full-screen chat.
      const target = `/chat/${apiKey}?from=portal`;
      // The staff chat only recognises a *signed-in* user: it forwards their
      // platform JWT as X-User-Token so the backend runs identity-bound tools
      // (e.g. create_jira_issue) under their identity. An anonymous visitor
      // here would silently be denied those tools and the assistant would
      // narrate progress it can't perform. Require sign-in first, then return
      // to the staff chat. (We only redirect once auth state is known, so an
      // already-signed-in user is never bounced to login.)
      if (!isAuthenticated && !authLoading) {
        setLocation(`/login?redirect=${encodeURIComponent(target)}`);
        return;
      }
      setLocation(target);
      return;
    }
    setLocation(isAuthenticated ? "/portal/staff" : "/login");
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-background to-muted/40">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-muted/40 flex flex-col">
      <header className="px-6 py-5 flex items-center justify-between">
        <Link href="/" data-testid="link-portal-home">
          <span className="flex items-center gap-2 text-sm font-semibold tracking-tight">
            <Sparkles className="w-4 h-4 text-primary" />
            {branding.shortName}
          </span>
        </Link>
      </header>

      <main className="flex-1 flex items-center justify-center px-6 py-10">
        <div className="w-full max-w-3xl">
          <div className="text-center mb-10">
            <h1 className="text-3xl font-bold tracking-tight" data-testid="text-gateway-title">
              {botName ? `Test ${botName}` : "How would you like to continue?"}
            </h1>
            <p className="text-muted-foreground mt-3 max-w-xl mx-auto">
              {apiKey
                ? "Open this assistant the way your audience will. Continue as a guest, or as a staff member to chat as yourself."
                : `Chat with ${branding.productName} as a guest, or sign in as a staff member to use your organisation's internal assistants.`}
            </p>
          </div>

          {apiKey && !staffEnabled && readinessMessage && (
            <div
              className="mb-5 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-950 whitespace-pre-line"
              role="alert"
              data-testid="alert-staff-portal-readiness"
            >
              {readinessMessage}
            </div>
          )}

          <div className="grid sm:grid-cols-2 gap-5">
            <PortalCard
              icon={<Users className="w-6 h-6" />}
              title="I'm a guest"
              description="Fill the contact form, then ask anything. No account needed."
              cta="Continue as guest"
              enabled={guestEnabled}
              disabledLabel="This assistant isn't open to the public"
              onClick={onGuest}
              testId="card-portal-public"
            />
            <PortalCard
              icon={<ShieldCheck className="w-6 h-6" />}
              title="I'm staff"
              description="Chat as yourself. The assistant tailors its answers to your role automatically."
              cta={apiKey ? "Open assistant" : isAuthenticated ? "Open staff portal" : "Sign in to continue"}
              enabled={staffEnabled}
              disabledLabel="Staff Portal is not available yet"
              onClick={onStaff}
              testId="card-portal-staff"
            />
          </div>
        </div>
      </main>
    </div>
  );
}

function PortalCard({
  icon,
  title,
  description,
  cta,
  enabled,
  disabledLabel,
  onClick,
  testId,
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
  cta: string;
  enabled: boolean;
  disabledLabel: string;
  onClick: () => void;
  testId: string;
}) {
  return (
    <Card
      className={`group border-2 ${
        enabled
          ? "hover-elevate active-elevate-2 cursor-pointer"
          : "opacity-60 cursor-not-allowed"
      }`}
      role="button"
      tabIndex={enabled ? 0 : -1}
      onClick={enabled ? onClick : undefined}
      onKeyDown={
        enabled
          ? (e: React.KeyboardEvent) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onClick();
              }
            }
          : undefined
      }
      aria-disabled={!enabled}
      aria-label={`${title}. ${description} ${enabled ? cta : disabledLabel}`}
      data-testid={testId}
    >
      <CardContent className="p-6 space-y-4">
        {/* Decorative color/icon tile — identity is conveyed by the heading text, not color (1.1.1 / 1.4.1) */}
        <div aria-hidden="true" className="w-12 h-12 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
          {enabled ? icon : <Lock className="w-6 h-6" />}
        </div>
        <div>
          <h2 className="text-lg font-semibold">{title}</h2>
          <p className="text-sm text-muted-foreground mt-1">{description}</p>
        </div>
        {enabled ? (
          <span className="inline-flex items-center gap-1.5 text-sm font-medium text-primary">
            {cta}
            <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
          </span>
        ) : (
          <span className="inline-flex items-center gap-1.5 text-sm font-medium text-muted-foreground">
            {disabledLabel}
          </span>
        )}
      </CardContent>
    </Card>
  );
}
